import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../services/apiClient';
import { LoadingScreen } from '../common/LoadingScreen';

interface SuperAdminGuardProps {
  children: React.ReactNode;
}

interface UserWithRoles {
  uuid: string;
  email: string;
  name: string;
  type: string;
  roles: string[];
}

/**
 * Super Admin authorization guard
 * Checks if the current user has SUPER_ADMIN role
 */
export const SuperAdminGuard: React.FC<SuperAdminGuardProps> = ({ children }) => {
  const { isAuthenticated, isAuthEnabled } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      // If auth is not enabled, deny access
      if (!isAuthEnabled) {
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      // If not authenticated, deny access
      if (!isAuthenticated) {
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      try {
        // Fetch current user with roles from the API
        const response = await apiClient.get<UserWithRoles>('/auth/user');
        const userRoles = response.roles || [];
        
        // Check if user has SUPER_ADMIN role
        setHasAccess(userRoles.includes('SUPER_ADMIN'));
      } catch (err) {
        console.error('Failed to check user roles:', err);
        setError('Failed to verify access permissions');
        setHasAccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAccess();
  }, [isAuthenticated, isAuthEnabled]);

  if (isLoading) {
    return <LoadingScreen message="Verifying access permissions..." />;
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Alert severity="warning" sx={{ maxWidth: 400 }}>
          Access Denied. You need Super Admin privileges to view this page.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Please contact your administrator if you believe you should have access.
        </Typography>
      </Box>
    );
  }

  // User has SUPER_ADMIN role, show protected content
  return <>{children}</>;
};